import { DateTime } from "luxon";
import { ET_ZONE } from "./get-next-swift-processing-date-in-utc";

/**
 * Re-expresses the ET calendar date of an instant at midnight in the user's local zone.
 *
 * Date pickers operate on local wall-clock dates, but payment processing runs on ET
 * calendar dates. Converting an instant to the local zone shifts the calendar date for
 * users east of ET (e.g. shortly after midnight IST it is still "yesterday" in ET), so
 * picker bounds must carry the ET date across zones with keepLocalTime instead.
 *
 * @example
 * // Browser in IST (UTC+5:30); the instant 2026-06-01T20:30Z is June 1, 4:30 PM in ET
 * // but already June 2, 2:00 AM in IST. The ET calendar date is June 1, so this returns
 * // 2026-06-01T00:00+05:30 (June 1 at IST midnight) — not June 2.
 */
export const etCalendarDateAtLocalMidnight = (instant: DateTime): DateTime => {
  return instant
    .setZone(ET_ZONE)
    .startOf("day")
    .setZone(DateTime.local().zoneName, { keepLocalTime: true });
};

/**
 * Today's ET calendar date as a JS Date at local midnight, for use as a DatePicker
 * bound or initial value. "Today" is the same ET date for every user worldwide.
 *
 * @example
 * // Browser in Honolulu (UTC-10) at 8:00 PM on June 1: ET has already rolled over to
 * // June 2, 2:00 AM, so this returns June 2 at HST midnight — the picker's earliest
 * // selectable day is June 2 even though the local calendar still says June 1.
 */
export const getTodayInETAsLocalDate = (): Date => {
  return etCalendarDateAtLocalMidnight(DateTime.now()).toJSDate();
};
